import { Printer, Warning } from "@phosphor-icons/react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MODULE_KEYS } from "@/features/onboarding/compartments";
import type { AdminHouseholdDetail } from "@/lib/api";

import { buildSheet } from "./kit-sheet/sheet-data";

interface Props {
  household: AdminHouseholdDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirms what is about to print before any paper is used.
 *
 * The point of the preview is the identity block. The sheet reads its name from
 * `contact_name` while the flow greets the household from their profile's
 * `first_name`, so those two can disagree, and the sheet is printed long before
 * a profile exists. Showing the admin the exact name, address and code that will
 * print is the cheap safeguard against a box going out addressed to the wrong
 * person.
 */
export function KitSheetDialog({ household, open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const sheet = buildSheet(household, window.location.origin);
  const hasCode = sheet.code !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Kit sheet</DialogTitle>
          <DialogDescription>
            Two A4 pages for this home&apos;s kit. Print double-sided: the scan page goes on the
            front, the box checklist on the back.
          </DialogDescription>
        </DialogHeader>

        <dl className="border-border-subtle divide-border-subtle divide-y rounded-xl border text-[13.5px]">
          <Row label="Prints for">{sheet.belongsTo}</Row>
          {sheet.belongsSub && <Row label="At">{sheet.belongsSub}</Row>}
          <Row label="Access code">
            {hasCode ? (
              <span className="font-mono tracking-[0.14em]">{sheet.codeDisplay}</span>
            ) : (
              <span className="text-destructive font-semibold">None issued</span>
            )}
          </Row>
          {/* Digits are fine here: this is a staff readout, not household copy. */}
          <Row label="Compartments">
            {sheet.compartments.length} of {MODULE_KEYS.length}, {sheet.totalTimeWords}
          </Row>
        </dl>

        {hasCode ? (
          <p className="text-muted flex items-start gap-2 text-[13px] leading-relaxed">
            <Warning className="text-warning mt-0.5 size-4 shrink-0" weight="light" />
            <span>
              Your browser&apos;s print dialog may add its own header and footer. Switch those off
              before saving, or a URL and a page number print onto the sheet.
            </span>
          </p>
        ) : (
          <p className="text-destructive flex items-start gap-2 text-[13px] leading-relaxed">
            <Warning className="mt-0.5 size-4 shrink-0" weight="light" />
            <span>
              This home has no access code, so the sheet has nothing to scan. Issue a code from the
              band above first.
            </span>
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/households/${household.id}/kit-sheet`)}
          >
            Preview
          </Button>
          <Button
            type="button"
            disabled={!hasCode}
            onClick={() => navigate(`/admin/households/${household.id}/kit-sheet?print=1`)}
          >
            <Printer className="size-4" weight="light" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <dt className="text-muted whitespace-nowrap">{label}</dt>
      <dd className="text-primary text-right font-medium">{children}</dd>
    </div>
  );
}
